import { useEffect, useRef, useState } from "react";
import api from "../../axiosconfig";

const STORAGE_PREFIX = "pearliTrendsOverview:";

const readSaved = (key) => {
    try {
        const raw = window.sessionStorage.getItem(STORAGE_PREFIX + key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const save = (key, overview) => {
    try {
        window.sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(overview));
    } catch {
        // Storage can be full or blocked; the overview still shows.
    }
};

/**
 * Monthly domain averages and the AI Well-Being Overview for one audience.
 *
 * `endpoint` returns the months, values and statuses; `${endpoint}/overview` returns the overview for the
 * same parameters. An AI overview is kept in sessionStorage under the data's `overviewKey`, so reloading
 * the page reuses it instead of generating it again. While a new overview loads, the previous one stays.
 */
export default function useMonthlyTrends(endpoint, params, enabled = true) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [overview, setOverview] = useState(null);
    const [overviewLoading, setOverviewLoading] = useState(false);
    const request = useRef(0);
    const paramsKey = JSON.stringify(params || {});

    useEffect(() => {
        if (!enabled) return undefined;
        const id = ++request.current;
        const current = () => id === request.current;
        const query = JSON.parse(paramsKey);

        const load = async () => {
            setLoading(true);
            setError(null);
            let monthly;
            try {
                monthly = (await api.get(endpoint, { params: query })).data;
            } catch (e) {
                if (!current()) return;
                console.error("[useMonthlyTrends] monthly trends request failed", e?.response?.status);
                const status = e?.response?.status;
                setError(status === 400 ? "Invalid range or filters. Please adjust your selection."
                    : status === 401 || status === 403 ? "You don't have access to these monthly trends."
                    : "Unable to load monthly trends. Please try again.");
                setData(null);
                setOverview(null);
                setLoading(false);
                return;
            }
            if (!current()) return;
            setData(monthly);
            setLoading(false);

            const saved = monthly?.overviewKey ? readSaved(monthly.overviewKey) : null;
            if (saved) {
                setOverview(saved);
                return;
            }
            setOverviewLoading(true);
            try {
                const result = (await api.get(`${endpoint}/overview`, { params: query })).data;
                if (!current()) return;
                setOverview(result);
                if (result?.source === "ai" && result.overviewKey) save(result.overviewKey, result);
            } catch (e) {
                if (!current()) return;
                console.error("[useMonthlyTrends] overview request failed", e?.response?.status);
                setOverview({ overviewKey: monthly?.overviewKey, basis: monthly?.basis, sections: [], failed: true });
            } finally {
                if (current()) setOverviewLoading(false);
            }
        };

        load();
    }, [endpoint, paramsKey, enabled]);

    return { data, loading, error, overview, overviewLoading };
}
